import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ListTodo, Edit2, Trash2, CheckCircle2, Clock, AlertTriangle, ArrowRightCircle, User, MessageSquare, Bell, Send, AtSign, FileDown, Target, CalendarPlus, Mail } from "lucide-react";
import ShareByEmailDialog from "@/components/ShareByEmailDialog";
import { useRowHighlight } from "@/lib/useRowHighlight";

const PRIORIDAD_COLORS = {
  baja: "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]",
  media: "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]",
  alta: "bg-[#fcf2eb] text-[#9a4920] border-[#e8b693]",
  urgente: "bg-[#fbe1de] text-[#b53a25] border-[#d99584]",
};

const ESTADO_COLORS = {
  pendiente: "bg-white text-[#5c7368] border-[#d1dbd6]",
  en_progreso: "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]",
  completada: "bg-[#205c45] text-white border-[#205c45]",
  cancelada: "bg-[#f3f4f6] text-[#8ba397] border-[#d1d5db] line-through",
};

export default function Tasks() {
  const { user } = useAuth();
  const isManager = user && (["admin", "gerencia"].includes(user.role) || (user.modulos || []).some(m => ["rrhh", "gerencia"].includes(m)));
  const { highlightId, setRef, flash } = useRowHighlight();

  const [view, setView] = useState("mias"); // mias | creadas | de_otros
  const [estadoFilter, setEstadoFilter] = useState("todos");
  const [tareas, setTareas] = useState([]);
  const [users, setUsers] = useState([]);
  const [otherUserFilter, setOtherUserFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  // Comentarios y menciones
  const [openComentariosId, setOpenComentariosId] = useState(null);
  const [comentarios, setComentarios] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [showMencionPicker, setShowMencionPicker] = useState(false);
  // Notificaciones in-app
  const [notifs, setNotifs] = useState([]);
  const [notifsCount, setNotifsCount] = useState(0);
  const [notifsAgrupadas, setNotifsAgrupadas] = useState(null);
  const [notifCatActiva, setNotifCatActiva] = useState("aprobacion");
  const [notifsOpen, setNotifsOpen] = useState(false);
  // PDF export
  const [pdfPeriodo, setPdfPeriodo] = useState("semanal");
  // Compartir por email + agregar a calendario
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarea, setShareTarea] = useState(null);

  const agregarAlCalendario = async (tarea) => {
    try {
      const { data } = await api.post(`/tareas/${tarea.id}/agregar-calendario`, { duracion_minutos: 60 });
      if (data.ya_existia) {
        toast.info("Esta tarea ya estaba en tu calendario");
      } else {
        toast.success("Agregada al calendario");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const abrirCompartir = (tarea) => {
    setShareTarea(tarea);
    setShareOpen(true);
  };

  function emptyForm() {
    return { titulo: "", descripcion: "", prioridad: "media", fecha_limite: "", asignado_a: user?.id || "", modulo: "", tags: "", recordatorio_at: "" };
  }

  const load = async () => {
    try {
      const params = {};
      if (view === "creadas") params.creadas_por_mi = true;
      else if (view === "de_otros" && otherUserFilter) params.asignado_a = otherUserFilter;
      else params.asignado_a = "me";
      if (estadoFilter !== "todos") params.estado = estadoFilter;
      const { data } = await api.get("/tareas", { params });
      setTareas(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, estadoFilter, otherUserFilter]);

  useEffect(() => {
    // Todos los usuarios necesitan la lista de usuarios para menciones @ en comentarios
    api.get("/users/basic").then(({ data }) => setUsers((data || []).filter(u => u.activo))).catch(() => {});
  }, []);

  const loadNotifs = async () => {
    try {
      const [{ data }, { data: g }] = await Promise.all([
        api.get("/tareas/notificaciones"),
        api.get("/tareas/notificaciones/agrupadas"),
      ]);
      setNotifs(data.items || []);
      setNotifsCount(data.no_leidas || 0);
      setNotifsAgrupadas(g || null);
    } catch (err) { console.warn("loadNotifs:", err); }
  };
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 60000);
    return () => clearInterval(t);
  }, []);

  // Cuando se abre el modal, salta a la primera categoría con no-leídas
  useEffect(() => {
    if (!notifsOpen || !notifsAgrupadas) return;
    const cats = notifsAgrupadas.categorias || [];
    const conNoLeidas = cats.find(c => (notifsAgrupadas.por_categoria?.[c.clave]?.no_leidas || 0) > 0);
    if (conNoLeidas) {
      setNotifCatActiva(conNoLeidas.clave);
    } else {
      // si no hay no leídas, mantener la actual o ir a la primera con items
      const conItems = cats.find(c => (notifsAgrupadas.por_categoria?.[c.clave]?.items?.length || 0) > 0);
      if (conItems) setNotifCatActiva(conItems.clave);
    }
  }, [notifsOpen, notifsAgrupadas]);

  const loadComentarios = async (tareaId) => {
    try {
      const { data } = await api.get(`/tareas/${tareaId}/comentarios`);
      setComentarios(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openComentarios = async (tarea) => {
    setOpenComentariosId(tarea.id);
    setNuevoComentario("");
    await loadComentarios(tarea.id);
  };

  const enviarComentario = async () => {
    if (!nuevoComentario.trim() || !openComentariosId) return;
    try {
      await api.post(`/tareas/${openComentariosId}/comentarios`, { texto: nuevoComentario.trim() });
      setNuevoComentario("");
      await loadComentarios(openComentariosId);
      toast.success("Comentario publicado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminarComentario = async (com) => {
    if (!window.confirm("¿Eliminar comentario?")) return;
    try {
      await api.delete(`/tareas/${openComentariosId}/comentarios/${com.id}`);
      await loadComentarios(openComentariosId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const insertarMencion = (u) => {
    const handle = (u.email || "").split("@")[0];
    const insert = `@${handle} `;
    setNuevoComentario(prev => prev + insert);
    setShowMencionPicker(false);
  };

  const marcarTodasLeidas = async () => {
    try {
      await api.post(`/tareas/notificaciones/marcar-todas-leidas`);
      loadNotifs();
    } catch (err) { console.warn("marcarTodasLeidas:", err); }
  };

  const irATareaDesdeNotif = async (n) => {
    try { await api.patch(`/tareas/notificaciones/${n.id}/leida`); } catch (err) { console.warn("marcar leida:", err); }
    setNotifsOpen(false);
    loadNotifs();
    // Si la notif tiene URL directa (rotación/caducidad), navegar ahí
    if (n.url && !n.tarea_id) {
      window.location.href = n.url;
      return;
    }
    // Buscar tarea y abrir comentarios
    const t = tareas.find(x => x.id === n.tarea_id);
    if (t) {
      openComentarios(t);
    } else if (n.tarea_id) {
      try {
        const { data } = await api.get("/tareas", { params: { asignado_a: "me" } });
        const found = (data || []).find(x => x.id === n.tarea_id);
        if (found) openComentarios(found);
      } catch (err) { console.warn("buscar tarea:", err); }
    }
  };

  // Renderiza texto con menciones resaltadas
  const renderConMenciones = (texto) => {
    const parts = [];
    let last = 0;
    const rx = /@([a-z0-9._-]+)/gi;
    let m;
    while ((m = rx.exec(texto || "")) !== null) {
      if (m.index > last) parts.push(texto.slice(last, m.index));
      parts.push(<span key={m.index} className="font-semibold text-[#205c45] bg-[#e9f2ee] rounded px-1">@{m[1]}</span>);
      last = m.index + m[0].length;
    }
    if (last < (texto || "").length) parts.push(texto.slice(last));
    return parts;
  };

  const descargarPDF = async () => {
    try {
      const resp = await api.get(`/tareas/reporte-pdf?periodo=${pdfPeriodo}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_tareas_${pdfPeriodo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Reporte ${pdfPeriodo} descargado`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      titulo: t.titulo || "",
      descripcion: t.descripcion || "",
      prioridad: t.prioridad || "media",
      fecha_limite: t.fecha_limite || "",
      recordatorio_at: (t.recordatorio_at || "").slice(0, 16),
      asignado_a: t.asignado_a || user.id,
      modulo: t.modulo || "",
      tags: (t.tags || []).join(", "),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.titulo.trim()) return toast.error("El título es obligatorio");
    try {
      const payload = {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion || null,
        prioridad: form.prioridad,
        fecha_limite: form.fecha_limite || null,
        recordatorio_at: form.recordatorio_at || null,
        asignado_a: form.asignado_a || null,
        modulo: form.modulo || null,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      let savedId = null;
      if (editing) {
        const res = await api.patch(`/tareas/${editing.id}`, payload);
        savedId = res?.data?.id || editing.id;
        toast.success("Tarea actualizada");
      } else {
        const res = await api.post("/tareas", payload);
        savedId = res?.data?.id;
        toast.success("Tarea creada");
      }
      setDialogOpen(false); setEditing(null);
      await load();
      if (savedId) flash(savedId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cambiarEstado = async (t, nuevoEstado) => {
    try {
      await api.patch(`/tareas/${t.id}`, { estado: nuevoEstado });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminar = async (t) => {
    if (!window.confirm(`¿Eliminar "${t.titulo}"?`)) return;
    try {
      await api.delete(`/tareas/${t.id}`);
      toast.success("Tarea eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const tareasOrdenadas = useMemo(() => {
    const orden = { urgente: 0, alta: 1, media: 2, baja: 3 };
    const ordenEstado = { pendiente: 0, en_progreso: 1, completada: 2, cancelada: 3 };
    return [...tareas].sort((a, b) => {
      if (a.estado !== b.estado) return ordenEstado[a.estado] - ordenEstado[b.estado];
      if (a.prioridad !== b.prioridad) return orden[a.prioridad] - orden[b.prioridad];
      if (a.fecha_limite && b.fecha_limite) return a.fecha_limite.localeCompare(b.fecha_limite);
      return 0;
    });
  }, [tareas]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Productividad</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Mis tareas</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Lista personal de tareas. {isManager ? "Como gerencia/RRHH puedes asignar tareas a otros usuarios." : "Crea tus propias tareas para organizar tu día."}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={pdfPeriodo} onValueChange={setPdfPeriodo}>
            <SelectTrigger className="w-32 h-9 text-xs" data-testid="pdf-periodo-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="diario">Diario</SelectItem>
              <SelectItem value="semanal">Semanal</SelectItem>
              <SelectItem value="mensual">Mensual</SelectItem>
              <SelectItem value="anual">Anual</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={descargarPDF} variant="outline" size="sm" data-testid="task-pdf-btn">
            <FileDown size={14} className="mr-1.5" /> PDF
          </Button>
          <button
            onClick={() => setNotifsOpen(true)}
            className="relative px-3 py-2 rounded-md border border-[#d1dbd6] hover:bg-[#e9f2ee] text-[#4a5c53]"
            title="Notificaciones"
            data-testid="task-notif-bell"
          >
            <Bell size={16} />
            {notifsCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#b53a25] text-white text-[10px] rounded-full min-w-[18px] h-[18px] grid place-items-center font-semibold">{notifsCount}</span>
            )}
          </button>
          <Button onClick={openCreate} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="task-create-btn">
            <Plus size={16} className="mr-2" /> Nueva tarea
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex bg-white border border-[#d1dbd6] rounded-md overflow-hidden text-sm">
          <button onClick={() => setView("mias")} className={`px-3 py-1.5 ${view === "mias" ? "bg-[#205c45] text-white" : "text-[#4a5c53]"}`} data-testid="task-view-mias">📥 Asignadas a mí</button>
          <button onClick={() => setView("creadas")} className={`px-3 py-1.5 ${view === "creadas" ? "bg-[#205c45] text-white" : "text-[#4a5c53]"}`} data-testid="task-view-creadas">✏️ Creadas por mí</button>
          {isManager && <button onClick={() => setView("de_otros")} className={`px-3 py-1.5 ${view === "de_otros" ? "bg-[#205c45] text-white" : "text-[#4a5c53]"}`} data-testid="task-view-otros">👥 De otros</button>}
        </div>

        {view === "de_otros" && (
          <Select value={otherUserFilter || "_none"} onValueChange={(v) => setOtherUserFilter(v === "_none" ? "" : v)}>
            <SelectTrigger className="w-64" data-testid="task-other-user-select"><SelectValue placeholder="Seleccionar usuario" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— Seleccionar —</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.nombres} {u.apellidos} ({u.role})</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger className="w-44" data-testid="task-estado-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="en_progreso">En progreso</SelectItem>
            <SelectItem value="completada">Completadas</SelectItem>
            <SelectItem value="cancelada">Canceladas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {tareasOrdenadas.length === 0 && (
          <div className="text-center py-12 bg-white rounded-md border border-dashed border-[#d1dbd6]">
            <ListTodo size={32} className="mx-auto mb-3 text-[#8ba397]" />
            <p className="text-[#4a5c53]">Sin tareas {estadoFilter !== "todos" ? `en estado "${estadoFilter}"` : "todavía"}.</p>
          </div>
        )}
        {tareasOrdenadas.map(t => {
          const vencida = t.fecha_limite && t.fecha_limite < today && !["completada", "cancelada"].includes(t.estado);
          return (
            <div key={t.id} ref={setRef(t.id)} className={`bg-white rounded-md border p-4 ${vencida ? "border-[#b53a25]" : "border-[#d1dbd6]"} ${highlightId === t.id ? "row-highlight" : ""}`} data-testid={`task-row-${t.id}`}>
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={t.estado === "completada"}
                  onCheckedChange={() => cambiarEstado(t, t.estado === "completada" ? "pendiente" : "completada")}
                  className="mt-1"
                  data-testid={`task-check-${t.id}`}
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className={`font-semibold ${t.estado === "completada" ? "line-through text-[#8ba397]" : "text-[#15241d]"}`}>
                        {t.titulo}
                      </h3>
                      {t.descripcion && <p className="text-sm text-[#4a5c53] mt-1 whitespace-pre-line">{t.descripcion}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openComentarios(t)}
                        title="Ver/agregar comentarios y menciones @"
                        className="h-8 gap-1.5 text-[#205c45] border-[#b2d0c2] hover:bg-[#e9f2ee]"
                        data-testid={`task-coment-${t.id}`}
                      >
                        <MessageSquare size={13} />
                        <span className="text-xs font-semibold">{t.comentarios_count > 0 ? t.comentarios_count : "Comentar"}</span>
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => abrirCompartir(t)} title="Compartir por correo (PDF)" data-testid={`task-share-${t.id}`}><Mail size={14} className="text-[#205c45]" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => agregarAlCalendario(t)} title="Agregar al calendario" data-testid={`task-tocal-${t.id}`}><CalendarPlus size={14} className="text-[#205c45]" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`task-edit-${t.id}`}><Edit2 size={14} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => eliminar(t)} data-testid={`task-delete-${t.id}`}><Trash2 size={14} className="text-[#b53a25]" /></Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                    <Badge className={`border ${PRIORIDAD_COLORS[t.prioridad]}`}>{t.prioridad}</Badge>
                    <Badge className={`border ${ESTADO_COLORS[t.estado]}`}>{t.estado.replace("_", " ")}</Badge>
                    {t.fecha_limite && (
                      <span className={`flex items-center gap-1 ${vencida ? "text-[#b53a25] font-semibold" : "text-[#5c7368]"}`}>
                        <Clock size={11} /> {t.fecha_limite}{vencida && <AlertTriangle size={11} />}
                      </span>
                    )}
                    {t.asignado_a !== t.creado_por && (
                      <span className="flex items-center gap-1 text-[#5c7368]"><User size={11} /> {t.asignado_a_nombre}</span>
                    )}
                    {t.creado_por !== user?.id && (
                      <span className="text-[#8ba397]">por {t.creado_por_nombre}</span>
                    )}
                    {t.modulo && <Badge variant="outline" className="text-[10px]">{t.modulo}</Badge>}
                    {(t.tags || []).map(tag => <Badge key={tag} variant="outline" className="text-[10px] bg-[#f7f9f8]">#{tag}</Badge>)}
                  </div>
                  {t.estado === "pendiente" && (
                    <Button size="sm" variant="ghost" className="mt-2 text-[#205c45]" onClick={() => cambiarEstado(t, "en_progreso")}>
                      <ArrowRightCircle size={12} className="mr-1" /> Iniciar
                    </Button>
                  )}
                  {t.estado === "en_progreso" && (
                    <Button size="sm" variant="ghost" className="mt-2 text-[#205c45]" onClick={() => cambiarEstado(t, "completada")}>
                      <CheckCircle2 size={12} className="mr-1" /> Completar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Sheet open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#d1dbd6]">
            <SheetTitle>{editing ? "Editar tarea" : "Nueva tarea"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-3">
            <div className="space-y-1.5"><Label>Título *</Label><Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="task-titulo-input" /></div>
            <div className="space-y-1.5"><Label>Descripción</Label><Textarea rows={3} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v })}>
                  <SelectTrigger data-testid="task-prioridad-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fecha límite</Label>
                <Input type="date" value={form.fecha_limite} onChange={(e) => setForm({ ...form, fecha_limite: e.target.value })} />
              </div>
            </div>
            {isManager && (
              <div className="space-y-1.5">
                <Label>Asignado a</Label>
                <Select value={form.asignado_a || user?.id} onValueChange={(v) => setForm({ ...form, asignado_a: v })}>
                  <SelectTrigger data-testid="task-asignado-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={user?.id || ""}>👤 Yo mismo</SelectItem>
                    {users.filter(u => u.id !== user?.id).map(u => <SelectItem key={u.id} value={u.id}>{u.nombres} {u.apellidos} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* ITER208: alerta programada */}
            <div className="space-y-1.5">
              <Label>Alerta emergente (fecha y hora)</Label>
              <Input type="datetime-local" value={form.recordatorio_at}
                onChange={(e) => setForm({ ...form, recordatorio_at: e.target.value })}
                data-testid="task-recordatorio-input" />
              <p className="text-[11px] text-[#5c7368]">
                Al llegar esa hora recibirás una notificación emergente (y push si están activadas).
                Déjalo vacío para no programar alerta.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Módulo (opcional)</Label>
                <Input value={form.modulo} onChange={(e) => setForm({ ...form, modulo: e.target.value })} placeholder="ventas, contabilidad..." />
              </div>
              <div className="space-y-1.5">
                <Label>Etiquetas (coma)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="urgente, cliente, mes-cierre" />
              </div>
            </div>
            </div>
          </div>
          <SheetFooter className="border-t border-[#d1dbd6] px-6 py-4 gap-2">
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45]" data-testid="task-save-btn">Guardar</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Diálogo de Comentarios + Menciones */}
      <Dialog open={!!openComentariosId} onOpenChange={(v) => { if (!v) { setOpenComentariosId(null); setShowMencionPicker(false); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle><MessageSquare className="inline mr-2" size={18} />Comentarios</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="task-comentarios-list">
            {comentarios.length === 0 && (
              <div className="text-center py-8 text-sm text-[#8ba397]">
                Aún no hay comentarios en esta tarea. Sé el primero en comentar.
              </div>
            )}
            {comentarios.map((c) => (
              <div key={c.id} className="border-l-4 border-[#205c45] pl-3 py-2 bg-[#f7f9f8] rounded" data-testid={`comment-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs text-[#5c7368]">
                    <b className="text-[#15241d]">{c.user_nombre}</b> · {(c.created_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                  {c.user_id === user?.id && (
                    <button onClick={() => eliminarComentario(c)} className="text-[#b53a25] hover:underline text-[11px]" data-testid={`comment-del-${c.id}`}>Eliminar</button>
                  )}
                </div>
                <div className="text-sm mt-1 whitespace-pre-line">{renderConMenciones(c.texto)}</div>
                {(c.menciones || []).length > 0 && (
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    {c.menciones.map(m => (
                      <span key={m.id} className="text-[10px] bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] rounded px-1.5 py-0.5">
                        <AtSign size={9} className="inline" /> {m.nombre}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-[#d1dbd6] pt-3 mt-2 space-y-2">
            <div className="relative">
              <Textarea
                rows={3}
                value={nuevoComentario}
                onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder="Escribe un comentario… usa @nombre para mencionar a alguien."
                className="pr-10"
                data-testid="comment-input"
              />
              <button
                onClick={() => setShowMencionPicker(s => !s)}
                className="absolute top-2 right-2 p-1 rounded hover:bg-[#e9f2ee] text-[#205c45]"
                title="Mencionar usuario"
                data-testid="comment-mention-btn"
              >
                <AtSign size={16} />
              </button>
              {showMencionPicker && (
                <div className="absolute right-2 top-10 z-10 w-72 max-h-64 overflow-y-auto bg-white border border-[#d1dbd6] rounded-md shadow-lg" data-testid="mention-picker">
                  <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#5c7368] border-b">Mencionar a…</div>
                  {users.filter(u => u.id !== user?.id).map(u => (
                    <button
                      key={u.id}
                      onClick={() => insertarMencion(u)}
                      className="w-full text-left px-3 py-1.5 hover:bg-[#e9f2ee] flex items-center gap-2 text-sm"
                      data-testid={`mention-pick-${u.id}`}
                    >
                      <AtSign size={12} className="text-[#205c45]" />
                      <span className="font-medium">{u.nombres} {u.apellidos}</span>
                      <span className="text-[10px] text-[#5c7368] ml-auto capitalize">{u.role}</span>
                    </button>
                  ))}
                  {users.filter(u => u.id !== user?.id).length === 0 && (
                    <div className="px-3 py-3 text-xs text-[#8ba397]">No hay otros usuarios disponibles.</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={enviarComentario} className="bg-[#205c45]" data-testid="comment-send-btn" disabled={!nuevoComentario.trim()}>
                <Send size={14} className="mr-1.5" /> Publicar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo de Notificaciones — categorizado */}
      <Dialog open={notifsOpen} onOpenChange={setNotifsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle><Bell className="inline mr-2" size={18} />Notificaciones {notifsCount > 0 && <span className="text-xs ml-1 text-[#b53a25]">({notifsCount} sin leer)</span>}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end mb-2">
            {notifsCount > 0 && (
              <button onClick={marcarTodasLeidas} className="text-xs text-[#205c45] hover:underline" data-testid="notif-mark-all">Marcar todas como leídas</button>
            )}
          </div>
          {/* Tabs por categoría */}
          {notifsAgrupadas?.categorias?.length > 0 && (
            <div className="flex flex-wrap gap-1 border-b border-[#e6ebe8] mb-3 -mx-1 px-1" data-testid="notif-cat-tabs">
              {notifsAgrupadas.categorias.map((c) => {
                const grp = notifsAgrupadas.por_categoria?.[c.clave];
                const total = grp?.items?.length || 0;
                const noLeidas = grp?.no_leidas || 0;
                const active = notifCatActiva === c.clave;
                return (
                  <button
                    key={c.clave}
                    onClick={() => setNotifCatActiva(c.clave)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-t border-b-2 transition ${active ? "border-[#205c45] text-[#205c45] bg-[#e9f2ee]" : "border-transparent text-[#5c7368] hover:bg-[#f5f8f6]"}`}
                    data-testid={`notif-cat-tab-${c.clave}`}
                  >
                    {c.label}
                    {total > 0 && (
                      <span className={`ml-1.5 inline-flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 min-w-[18px] ${noLeidas > 0 ? "bg-[#b53a25] text-white" : "bg-[#d1dbd6] text-[#15241d]"}`} data-testid={`notif-cat-count-${c.clave}`}>
                        {noLeidas > 0 ? noLeidas : total}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {/* Items de la categoría activa */}
          <div className="space-y-2" data-testid="notif-list">
            {(() => {
              const items = notifsAgrupadas?.por_categoria?.[notifCatActiva]?.items || [];
              if (items.length === 0) {
                return <div className="text-center py-6 text-sm text-[#8ba397]" data-testid="notif-empty">No hay notificaciones en esta categoría.</div>;
              }
              return items.map((n) => {
                const isMention = n.tipo === "mencion_tarea";
                const isRot = n.tipo === "rotacion_critica";
                const isCad = n.tipo === "caducidad_lote";
                const isLiq = n.tipo === "liquidacion_viatico";
                let bg = "bg-white border-[#d1dbd6]";
                if (!n.leida) {
                  if (isRot) bg = "bg-[#fdecea] border-[#d99584]";
                  else if (isCad) bg = "bg-[#fef4ec] border-[#f5b277]";
                  else if (isLiq) bg = "bg-[#e9f2ee] border-[#b2d0c2]";
                  else bg = "bg-[#fef7ec] border-[#f5c477]";
                } else {
                  bg = "bg-white border-[#e6ebe8] opacity-70";
                }
                return (
                  <button
                    key={n.id}
                    onClick={() => irATareaDesdeNotif(n)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${bg}`}
                    data-testid={`notif-${n.id}`}
                  >
                    <div className="text-xs text-[#5c7368]">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    <div className="text-sm font-medium text-[#15241d] mt-0.5">
                      {isMention && <><AtSign size={12} className="inline text-[#205c45]" /> <b>{n.autor_nombre}</b> te mencionó en <em>"{n.tarea_titulo}"</em></>}
                      {isRot && <><Target size={12} className="inline text-[#b53a25]" /> {n.tarea_titulo}</>}
                      {isCad && <><AlertTriangle size={12} className="inline text-[#8a5500]" /> {n.tarea_titulo}</>}
                      {!isMention && !isRot && !isCad && (n.titulo || n.tarea_titulo)}
                    </div>
                    <div className="text-xs text-[#4a5c53] mt-1 line-clamp-2">{n.texto_preview}</div>
                  </button>
                );
              });
            })()}
          </div>
        </DialogContent>
      </Dialog>

      <ShareByEmailDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        tipo="tarea"
        item={shareTarea}
        endpoint="/tareas/{id}/enviar-pdf"
      />
    </div>
  );
}
